// PilotManager.js
import React, { useState, useEffect } from "react";
import "./PilotManager.css";

export default function PilotManager() {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
  name: "",
  startAt: "",
  endAt: "",
  maxUsers: 30,
  inviteMessage:
      "You are invited to take part in a 30-day fluency growth pilot. Your speaking data and progress will be used anonymously to improve the platform. Participation is voluntary, and you can withdraw anytime.",
  });

  // Fetch all pilots
  useEffect(() => {
    fetch("https://fluency.ispeakwell.ca/api/pilots")
      .then((r) => r.json())
      .then((list) => setPilots(list))
      .catch((err) => console.error("Error fetching pilots:", err))
      .finally(() => setLoading(false));
  }, []);

    const createPilot = async () => {
    if (!form.name || !form.startAt || !form.endAt) {
        alert("Please fill all required fields (name, start, end)");
        return;
    }

    // Ensure maxUsers ≥ 1
    const safeMax = Math.max(1, parseInt(form.maxUsers || 1, 10));

    setCreating(true);
    await fetch("https://fluency.ispeakwell.ca/api/pilots/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            name: form.name,
            startAt: form.startAt,
            endAt: form.endAt,
            maxUsers: safeMax,
            inviteMessage: form.inviteMessage,
        }),
    });
    const list = await fetch("https://fluency.ispeakwell.ca/api/pilots").then((r) => r.json());
    setPilots(list);
    setCreating(false);
    };

  const togglePilot = async (pilot) => {
    const now = new Date();

    // Early ON / OFF alerts
    const startDate = new Date(pilot.startAt._seconds * 1000);
    const earlyWindow = new Date(startDate);
    earlyWindow.setDate(earlyWindow.getDate() - 30); // 30-day pre-launch window

    if (!pilot.active && now < earlyWindow) {
    alert(`You can only activate this pilot within 30 days of its start date.`);
    return;
    }
    if (pilot.active && now < new Date(pilot.endAt._seconds * 1000)) {
      if (!window.confirm(`Pilot "${pilot.name}" hasn’t ended yet. Turn off early?`))
        return;
    }

    // Request toggle
    const res = await fetch("https://fluency.ispeakwell.ca/api/pilots/toggle", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pilotId: pilot.id, makeActive: !pilot.active }),
    });
    const data = await res.json();

    if (data.conflict) {
      if (
        window.confirm(
          `Another pilot (“${data.activePilot}”) is active. Deactivate it and activate this one?`
        )
      ) {
        await fetch("https://fluency.ispeakwell.ca/api/pilots/toggle", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pilotId: pilot.id, makeActive: true }),
        });
      }
    }

    const list = await fetch("https://fluency.ispeakwell.ca/api/pilots").then((r) => r.json());
    setPilots(list);
  };

  if (loading) return <p style={{ color: "#fff" }}>Loading pilots...</p>;

  return (
    <div className="pilot-manager" style={{ width: "100%" }}>
      <h3 style={{ marginTop: 0 }}>Pilot Manager</h3>

      {/* --- Create Pilot --- */}
      <div style={{ marginBottom: "1rem" }}>
        <input
          placeholder="Pilot Name"
          style={{ width: "100%", marginBottom: "6px" }}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <label>Start Date:</label>
        <input
          type="date"
          style={{ width: "100%", marginBottom: "6px" }}
          onChange={(e) => setForm({ ...form, startAt: e.target.value })}
        />
        <label>End Date:</label>
        <input
          type="date"
          style={{ width: "100%", marginBottom: "6px" }}
          onChange={(e) => setForm({ ...form, endAt: e.target.value })}
        />
        <label>Maximum Users:</label>
        <input
          type="number"
          min="1"
          step="1"
          placeholder="Max Users (≥1)"
          value={form.maxUsers}
          onChange={(e) => setForm({ ...form, maxUsers: e.target.value })}
          style={{ width: "100%", marginBottom: "8px" }}
        />
        <label style={{ display: "block", marginTop: "8px" }}>Invitation Message</label>
        <textarea
        placeholder="Enter the message learners will see when joining this pilot..."
        value={form.inviteMessage}
        onChange={(e) => setForm({ ...form, inviteMessage: e.target.value })}
        rows={4}
        style={{
            width: "100%",
            resize: "vertical",
            borderRadius: "8px",
            border: "none",
            padding: "10px",
            color: "#00b894",
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(3px)",
            fontFamily: "Inter, sans-serif",
            marginBottom: "8px",
        }}
        />
        <button
          onClick={createPilot}
          disabled={creating}
          style={{
            width: "100%",
            background: "#007bff",
            border: "none",
            color: "#fff",
            padding: "8px",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          {creating ? "Creating..." : "Create Pilot"}
        </button>
      </div>

      {/* --- List Pilots --- */}
      {pilots.map((p) => (
        <div
          key={p.id}
          style={{
            background: p.active ? "#00b894" : "#555",
            padding: "8px",
            borderRadius: "6px",
            marginBottom: "6px",
          }}
        >
          {new Date(p.startAt._seconds * 1000).toDateString()} →{" "}
          {new Date(p.endAt._seconds * 1000).toDateString()}
          <br />
          Users: {p.userCount ?? 0} / {p.maxUsers}
          <div style={{ marginTop: "4px" }}>
            <button
              onClick={() => togglePilot(p)}
              style={{
                background: "#111",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                padding: "5px 10px",
                cursor: "pointer",
              }}
            >
              {p.active ? "🟢 Turn OFF" : "⚪ Turn ON"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
